type VisibleWrappedTagCountArgs = {
  /** Natural (unclipped) width of each chip, in source order. */
  readonly chipWidths: readonly number[];
  readonly gapPx: number;
  readonly availableWidth: number;
  /** Width of the "+N" button, reserved on the last line whenever a chip has to be dropped. */
  readonly overflowWidth: number;
  /** How many wrapped lines of chips the caller is willing to show. */
  readonly maxLines: number;
};

/**
 * Multi-line counterpart to `visibleInlineTagCount`: how many chips survive a flex-wrap row that is
 * clamped to `maxLines`. Same contract — the caller caches natural widths from a measuring pass, so
 * this stays pure and never has to read widths back off a row it has already clipped.
 *
 * The button is only reserved once something is known to overflow, which is why the fit is computed
 * twice: an unreserved pass answers "does everything fit", and only its failure pays for the button.
 */
export function visibleWrappedTagCount({
  chipWidths,
  gapPx,
  availableWidth,
  overflowWidth,
  maxLines,
}: VisibleWrappedTagCountArgs): number {
  if (chipWidths.length === 0 || maxLines <= 0) return 0;
  // Pre-measurement, the caller has no width to offer and wants every chip rendered so it can read
  // them back.
  if (availableWidth <= 0) return chipWidths.length;

  const unreserved = countWithinLines({ chipWidths, gapPx, availableWidth, maxLines, reserve: 0 });
  if (unreserved === chipWidths.length) return chipWidths.length;

  return countWithinLines({ chipWidths, gapPx, availableWidth, maxLines, reserve: overflowWidth });
}

function countWithinLines({
  chipWidths,
  gapPx,
  availableWidth,
  maxLines,
  reserve,
}: {
  readonly chipWidths: readonly number[];
  readonly gapPx: number;
  readonly availableWidth: number;
  readonly maxLines: number;
  readonly reserve: number;
}): number {
  let lineIndex = 0;
  let lineWidth = 0;
  let count = 0;

  for (const width of chipWidths) {
    if (count === 0) {
      // A chip wider than the container still renders and still occupies its line, so the first one
      // is never wrapped away on width. The reserve check below can still drop it when maxLines is 1
      // and the "+N" button won't fit beside it — then every chip lives in the overflow.
      lineWidth = width;
    } else if (lineWidth + gapPx + width <= availableWidth) {
      lineWidth += gapPx + width;
    } else {
      lineIndex += 1;
      if (lineIndex >= maxLines) return count;
      lineWidth = width;
    }

    const isLastLine = lineIndex === maxLines - 1;
    if (isLastLine && reserve > 0 && lineWidth + gapPx + reserve > availableWidth) return count;

    count += 1;
  }

  return count;
}
